import React, { useState, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faCheck, faLock } from "@fortawesome/free-solid-svg-icons";
import DynamicLayout from "../../../components/DynamicLayout";
import ProgressBar from "../../components/ProgressBar";
import { AcademyContext } from "../../../context/academyContext";
import saturnwallpaper from "../../../img/saturnwallpaper.jpg";
import { useNavigate } from "react-router-dom";

const TarotAcademy = () => {
  const { progress } = useContext(AcademyContext);
  const [activeChapter, setActiveChapter] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [selectedMode, setSelectedMode] = useState("beginner");
  const navigate = useNavigate();

  const totalLessons = 12;

  const toggleChapter = (chapter) => {
    setActiveChapter(activeChapter === chapter ? null : chapter);
  };

  const toggleLesson = (lesson) => {
    setActiveLesson(activeLesson === lesson ? null : lesson);
  };

  const renderIcon = (chapter, isAvailable) => {
    if (isAvailable) {
      return <FontAwesomeIcon icon={faCheck} className="has-text-warning ml-2" />;
    }
    return <FontAwesomeIcon icon={activeChapter === chapter ? faChevronDown : faChevronRight} className="ml-2" />;
  };

  const renderLessonIcon = (lesson, isAvailable) => {
    if (isAvailable) {
      return <FontAwesomeIcon icon={faCheck} className="has-text-warning ml-2" />;
    }
    return <FontAwesomeIcon icon={activeLesson === lesson ? faChevronDown : faChevronRight} className="ml-2" />;
  };

  const renderCheckMark = (lessonNumber) => {
    if (progress.tarot?.completedLessons >= lessonNumber) {
      return <FontAwesomeIcon icon={faCheck} className="has-text-warning ml-2" />;
    }
    return null;
  };

  const handleResumeClick = () => {
    const nextLesson = (progress.tarot?.completedLessons || 0) + 1;
    navigate(`/academy/tarot/${selectedMode}/lesson?lesson=${nextLesson}`);
  };

  const handleModeChange = (mode) => {
    setSelectedMode(mode);
  };

  const handleLessonClick = (lessonNumber) => {
    navigate(`/academy/tarot/${selectedMode}/lesson?lesson=${lessonNumber}`);
  };

  const renderLesson = (lessonNumber, lessonTitle) => {
    const isCompleted = progress.tarot?.completedLessons >= lessonNumber;
    const isAccessible = progress.tarot?.completedLessons >= lessonNumber - 1;
    
    return (
      <li
        className={`${isCompleted ? "has-text-warning" : isAccessible ? "has-text-grey" : "has-text-grey-light"} `}
        onClick={() => isAccessible && handleLessonClick(lessonNumber)}
        style={{ cursor: isAccessible ? 'pointer' : 'not-allowed' }}
      >
          {lessonTitle} {renderCheckMark(lessonNumber)}
        {!isAccessible && <FontAwesomeIcon icon={faLock} className="has-text-grey-light ml-2" />}
      </li>
    );
  };

  const hasStartedCourse = progress.tarot?.completedLessons > 0;
  const isHardModeUnlocked = true; // Temporarily enable Intermediate mode for development

  return (
    <DynamicLayout>
      <div
        className="full-screen-image-container heightauto margin-top-0"
        style={{
          backgroundImage: `url(${saturnwallpaper})`,
        }}
      >
        <section className="section hasbackgroundgrey heightauto has-text-centered tarot-course-page">
          <h2 className="title has-text-centered has-text-warning">
            {hasStartedCourse ? "Welcome back to the Tarot Course." : "Welcome to the Tarot Course."}
          </h2>
        
          <ProgressBar completedLessons={progress.tarot?.completedLessons || 0} totalLessons={totalLessons} />
          
          <ul>
            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.tarot?.completedLessons >= 1 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter1")}
                style={{ cursor: 'pointer' }}
              >
                Chapter 1: Introduction {renderIcon("chapter1", progress.tarot?.completedLessons >= 1)}
              </h2>
              <ul className={activeChapter === "chapter1" ? "" : "is-hidden"}>
                {renderLesson(1, "Lesson 1: Structure of the Tarot Deck")}
              </ul>
            </li>

            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.tarot?.completedLessons >= 2 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter2")}
                style={{ cursor: 'pointer' }}
              >
                Chapter 2: Major Arcana {renderIcon("chapter2", progress.tarot?.completedLessons >= 2)}
              </h2>
              <ul className={activeChapter === "chapter2" ? "" : "is-hidden"}>
                {renderLesson(2, "Lesson 1: The Fool")}
                {renderLesson(3, "Lesson 2: The Magician")}
                {renderLesson(4, "Lesson 3: The High Priestess")}
                {renderLesson(5, "Lesson 4: The Empress")}
                {renderLesson(6, "Lesson 5: The Emperor")}
                {renderLesson(7, "Lesson 6: The Hierophant")}
                {renderLesson(8, "Lesson 7: The Lovers")}
                {renderLesson(9, "Lesson 8: The Chariot")}
              </ul>
            </li>

            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.tarot?.completedLessons >= 12 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter3")}
                style={{ cursor: 'pointer' }}
              >
                Chapter 3: The Fool's Journey {renderIcon("chapter3", progress.tarot?.completedLessons >= 12)}
              </h2>
              <ul className={activeChapter === "chapter3" ? "" : "is-hidden"}>
                {renderLesson(12, "Lesson 1: Video Lesson")}
              </ul>
            </li>
          </ul>

          <button className="button is-warning mt-6 tarot-primary-action" onClick={handleResumeClick}>
            {hasStartedCourse ? "Resume" : "Start"}
          </button>

          <div className="mt-6 buttons has-addons is-centered tarot-mode-switch">
            <button
              className={`button is-small tarot-mode-btn ${selectedMode === "beginner" ? "is-warning" : ""}`}
              onClick={() => handleModeChange("beginner")}
            >
              Beginner
            </button>
            <button
              className={`button is-small tarot-mode-btn ${selectedMode === "intermediate" ? "is-warning" : ""} ${isHardModeUnlocked ? '' : 'is-locked'}`}
              onClick={() => handleModeChange("intermediate")}
              disabled={!isHardModeUnlocked}
              title={isHardModeUnlocked ? '' : 'Complete all lessons to unlock Intermediate mode'}
            >
              {!isHardModeUnlocked && <FontAwesomeIcon icon={faCheck} className="lock-icon" />}
              Intermediate
            </button>
          </div>
        </section>
      </div>
    </DynamicLayout>
  );
};

export default TarotAcademy;
